import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Avatar, Button} from '@mui/material';
import {Person} from '@mui/icons-material';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {useAuth} from '../../contexts/auth/context';
import {useOffice} from '../../contexts/office/context';
import {useSeniorSidebar} from '../../contexts/seniors-sidebar/context';
import {ContentPage} from '../content-page/content-page';
import {LoadingContent} from '../loading-content/loading-content';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {PhoneNumberInput} from '../phone-number-input/phone-number-input';
import {EditPicture} from '../edit-picture/edit-picture';
import {OccupationInput, OccupationOption} from '../occupation-input/occupation-input';
import {
  SocialNetworkCityInput,
  SocialNetworkCityOption,
} from '../social-network-city-input/social-network-city-input';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {FlatMultiTextSelect} from '../flat-multi-text-select/flat-multi-text-select';
import {Option} from '../select-dropdown/select-dropdown';
import {Affiliation} from '../../backend/interfaces/affiliation';
import {User} from '../../backend/interfaces/user';
import {Occupation} from '../../backend/interfaces/occupation';
import {SocialNetworkCity} from '../../backend/interfaces/social-network-city';
import {Religion, UserGender, RegionCode, BackendLanguageTag} from '../../backend/enums';
import {PutUser} from '../../backend/requests/put-user';
import {PostSenior} from '../../backend/requests/post-senior';
import {MaritalStatus} from '../../backend/enums/marital-status';
import {EducationLevel} from '../../backend/enums/education-level';
import {UserFormType} from '../../enums/user-form-type';
import {UsersRepository} from '../../repositories/users';
import {OccupationsRepository} from '../../repositories/occupations';
import {SocialNetworkCitiesRepository} from '../../repositories/social-network-cities';
import {EntitiesRepository} from '../../repositories/entities';
import {EntityUsersRepository} from '../../repositories/entity-users';
import {parsePhoneNumber, PhoneNumber} from 'libphonenumber-js/max';
import {
  FULL_NAME_MAX_CHARACTERS,
  DISPLAY_NAME_MAX_CHARACTERS,
  appSizes,
  ROOM_NAME_MAX_CHARACTERS,
  avatarSizesAndCoefficients,
  PROCESS_NUMBER_MAX_CHARACTERS,
  RECURRING_FEE_MAX_CHARACTERS,
  ADITIONAL_INFO_MAX_CHARACTERS,
} from '../../utils/constants';
import {
  languages,
  genders,
  religions,
  regions,
  maritalStatuses,
  educationLevels,
} from '../../utils/data';
import {convertArrayToIndex} from '../../utils/functions';
import {useEntity} from '../../contexts/entity/context';
import {
  emailValidation,
  nameValidation,
  nationalHealthServiceNumberValidation,
  phoneNumberValidation,
  taxPayerNumberValidation,
  socialSecurityNumberValidation,
  roomValidation,
  nationalIdNumberValidation,
} from './user-form-validations';
import {processProfilePicture} from './user-form-utils';
import {FlatSelectDate} from '../flat-select-date/flat-select-date';
import {getLocalizedFieldValue} from '../../utils/functions/get-localized-field-value';
import './user-senior-form.scss';
import {UserType} from '../../backend/enums/user-type';

interface SearchSelectInputItem {
  id: string;
  name: string;
  picture?: string;
}

type PhoneNumberExtension = {
  phone: string;
  code: string;
};

interface UserFormProps {
  userFormType:
    | UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver
    | UserFormType.CreateEditRemoveSenior_SystemAdministrator
    | UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator;
  redirectUrl: string;
  entityId?: string;
  entityName?: string;
  canEditRole?: boolean;
}

/**
 * Form to view, create, edit, and remove users (seniors, caregivers, current user):
 * - Read: all user types
 * - Create: seniors
 * - Edit: seniors, caregivers, and current user
 * - Remove: seniors
 *
 * It uses the office context to get the user's information.
 *
 * Note: In the future, maybe we will need to add more user form types to set read and write
 * permissions for the senior's profile because it may depend on the current user permissions.
 */
export const UserSeniorForm = (props: UserFormProps) => {
  let history = useHistory();
  const {t} = useTranslation();
  const defaultPhoneNumberExtension: PhoneNumberExtension = {
    phone: '+351',
    code: 'PT',
  };
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const authContext = useAuth();
  const {state: entityState} = useEntity();
  const seniorSidebarContext = useSeniorSidebar();
  const [model, setModel] = useState<User | undefined>();
  const [originalModel, setOriginalModel] = useState<User | undefined>();
  const [countryCode, setCountryCode] = useState<PhoneNumberExtension>(defaultPhoneNumberExtension);
  const [occupation, setOccupation] = useState<OccupationOption | undefined>();
  const [socialNetworkCity, setSocialNetworkCity] = useState<SocialNetworkCityOption | undefined>();
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>();

  // The entity is necessary for:
  // - System admin to create a senior (field in form)
  // - Professional caregiver to create a senior (passed by parameter)
  // - Professional caregiver to delete a senior and a caregiver (passed by parameter)
  // - Professional caregiver to update an affiliation type (passed by parameter)
  const [entity, setEntity] = useState<SearchSelectInputItem | undefined>(
    props.entityId
      ? {
          id: props.entityId,
          name: props.entityName || '',
        }
      : undefined
  );
  const [modelAffiliationData, setModelAffiliationData] = useState<Affiliation | undefined>();
  const [originalModelAffiliationData, setOriginalModelAffiliationData] = useState<
    Affiliation | undefined
  >();

  const [originalRolesIds, setOriginalRolesIds] = useState<string[] | undefined>(undefined);
  const [rolesIds, setRolesIds] = useState<string[] | undefined>(undefined);
  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [fullNameError, setFullNameError] = useState<string | undefined>();
  const [showFullNameError, setShowFullNameError] = useState<boolean>(false);
  const [displayNameError, setDisplayNameError] = useState<string | undefined>();
  const [showDisplayNameError, setShowDisplayNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [taxPayerNumberError, setTaxPayerNumberError] = useState<string | undefined>();
  const [showTaxPayerNumberError, setShowTaxPayerNumberError] = useState<boolean>(false);
  const [nationalHealthServiceNumberError, setNationalHealthServiceNumberError] = useState<
    string | undefined
  >();
  const [showNationalHealthServiceNumberError, setShowNationalHealthServiceNumberError] =
    useState<boolean>(false);
  const [socialSecurityNumberError, setSocialSecurityNumberError] = useState<string | undefined>();
  const [showSocialSecurityNumberError, setShowSocialSecurityNumberError] =
    useState<boolean>(false);

  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);
  const [entityError, setEntityError] = useState<string | undefined>();
  const [showEntityError, setShowEntityError] = useState<boolean>(false);
  const [roomError, setRoomError] = useState<string | undefined>();
  const [showRoomError, setShowRoomError] = useState<boolean>(false);
  const [showNationalIdNumberError, setShowNationalIdNumberError] = useState<boolean>(false);
  const [nationalIdNumberError, setNationalIdNumberError] = useState<string | undefined>();
  const [showAdditionalInfoError, setShowAdditionalInfoError] = useState<boolean>(false);
  const [additionalInfoError, setAdditionalInfoError] = useState<string | undefined>();
  const [showProcessNumberError, setShowProcessNumberError] = useState<boolean>(false);
  const [processNumberError, setProcessNumberError] = useState<string | undefined>();
  const [showRecurringFeeError, setShowRecurringFeeError] = useState<boolean>(false);
  const [recurringFeeError, setRecurringFeeError] = useState<string | undefined>();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);

  // Set user
  useEffect(() => {
    const init = async () => {
      if (officeContext.state.userForm) {
        // Init buttons state
        officeContext.setUserFormDeleteButtonState(SubmitButtonState.Normal);
        officeContext.setUserFormSubmitButtonState(SubmitButtonState.Normal);

        // Init some state
        setProfilePictureFile(undefined);
        setDataChanged(false);

        // Set models for edit/view user
        if (officeContext.state.userForm.user.id) {
          // Get occupation detail from backend
          if (officeContext.state.userForm.user.occupationId) {
            try {
              // Fetch occupation details
              const occupationDetails: Array<Occupation> =
                await OccupationsRepository.instance.getById(
                  officeContext.state.userForm.user.occupationId,
                  true
                );

              // Find occupation
              const userOccupation: Occupation | undefined =
                occupationDetails && Array.isArray(occupationDetails)
                  ? occupationDetails.find(
                      (obj) => obj.id === officeContext.state.userForm?.user.occupationId
                    )
                  : undefined;

              if (userOccupation && userOccupation.id) {
                // Set state
                setOccupation({
                  occupationId: userOccupation.id,
                  occupationDesignation: userOccupation.designation,
                });
              } else {
                // Error
                throw new Error('Empty occupation.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get occupation details.', error);
            }
          }

          // Get city detail from backend
          if (officeContext.state.userForm.user.socialNetworkCityId) {
            try {
              // Fetch city details
              const cityDetails: Array<SocialNetworkCity> =
                await SocialNetworkCitiesRepository.instance.getById(
                  officeContext.state.userForm.user.socialNetworkCityId,
                  true
                );

              // Find city
              const userCity: SocialNetworkCity | undefined =
                cityDetails && Array.isArray(cityDetails)
                  ? cityDetails.find(
                      (c) => c.id === officeContext.state.userForm?.user.socialNetworkCityId
                    )
                  : undefined;

              if (userCity && userCity.id) {
                // Set state
                setSocialNetworkCity({
                  cityId: userCity.id,
                  cityName: userCity.name,
                  countryName: userCity.countryName || '',
                });
              } else {
                // Error
                throw new Error('Empty city.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get city details.', error);
            }
          }

          // Get Senior affiliation data
          if (props.entityId) {
            const userAffiliationDetails = await EntityUsersRepository.instance.getById(
              props.entityId,
              officeContext.state.userForm.user.id
            );

            setModelAffiliationData(userAffiliationDetails.affiliation);
            setOriginalModelAffiliationData(userAffiliationDetails.affiliation);
          }

          if (officeContext.state.userForm.affiliation) {
            // Set roles ids
            setRolesIds(officeContext.state.userForm.affiliation.rolesIds);
            setOriginalRolesIds(officeContext.state.userForm.affiliation.rolesIds);
          }

          // Note: The original model will have the complete phone number (ex: "+351910000000").
          // The model will only have the phone number without the country calling code.

          // Parse phone number
          let parsedPhoneNumber: PhoneNumber | undefined;
          if (officeContext.state.userForm.user.phoneNumber) {
            try {
              parsedPhoneNumber = parsePhoneNumber(officeContext.state.userForm.user.phoneNumber);
            } catch (error) {
              // Log
              console.debug('🔴 Could not parse phone number in UserForm.', error);
            }
          }

          // Model
          const userModel: User = {
            ...officeContext.state.userForm.user,
            phoneNumber:
              parsedPhoneNumber && parsedPhoneNumber.isValid()
                ? parsedPhoneNumber.nationalNumber.toString()
                : undefined,
          };

          // Set models
          setOriginalModel(officeContext.state.userForm.user);
          setModel(userModel);
          officeContext.setUserFormLoadState(false);

          // Country code and international phone number
          if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
            // Set country code
            setCountryCode({
              phone: `+${parsedPhoneNumber.countryCallingCode.toString()}`,
              code: parsedPhoneNumber.country?.toString() || '',
            });
          }
        } else {
          // Set models for create user
          setOriginalModel(officeContext.state.userForm.user);
          setModel(officeContext.state.userForm.user);
          officeContext.setUserFormLoadState(false);
        }

        // Log
        console.debug('🆕 User set in UserForm component.');
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 User form button states cleaned in office context.');

      // Clear button states
      officeContext.clearUserFormDeleteButtonState();
      officeContext.clearUserFormSubmitButtonState();
      officeContext.setUserFormLoadState(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.userForm]);

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      let dataWasUpdated: boolean = false;

      // ----- Fields validation -----
      // Hold validation
      let fullNameValid: boolean = false;
      let phoneNumberValid: boolean = false;
      let emailValid: boolean = false;
      let taxPayerNumberValid: boolean = false;
      let displayNameValid: boolean = false;
      let entityValid: boolean = false;
      let roomValid: boolean = false;
      let nationalHealthServiceNumberValid: boolean = true;
      let socialSecurityNumberValid: boolean = true;
      let processNumberValid: boolean = true;
      let recurringFeeValid: boolean = true;
      let aditionalInfoValid: boolean = true;
      let nationalIdNumberValid: boolean = true;

      // #region ----- Fields validation -----
      // Full name validation
      const fullNameValidation = nameValidation(FULL_NAME_MAX_CHARACTERS, model.name);
      fullNameValid = fullNameValidation.valid;
      if (fullNameValidation.valid) {
        setFullNameError(undefined);
      } else if (fullNameValidation.error) {
        setFullNameError(t([fullNameValidation.error], {max: fullNameValidation.max}));
      }

      //Display name validation
      const displayNameValidation = nameValidation(DISPLAY_NAME_MAX_CHARACTERS, model.displayName);
      displayNameValid = displayNameValidation.valid;
      if (displayNameValidation.valid) {
        setDisplayNameError(undefined);
      } else if (displayNameValidation.error) {
        setDisplayNameError(t([displayNameValidation.error], {max: displayNameValidation.max}));
      }

      // Phone number validation
      const phoneValidation = phoneNumberValidation(
        countryCode.phone,
        props.userFormType,
        model.phoneNumber
      );
      phoneNumberValid = phoneValidation.valid;
      if (phoneValidation.valid) {
        setPhoneNumberError(undefined);
      } else if (!phoneValidation.valid && phoneValidation.error) {
        setPhoneNumberError(t([phoneValidation.error]));
      }

      // Email validation
      const validationEmail = emailValidation(model.email);
      emailValid = validationEmail.valid;
      if (validationEmail.error) {
        setEmailError(t([validationEmail.error]));
      } else {
        setEmailError(undefined);
      }

      // Tax payer number validation
      const taxPayerValidation = taxPayerNumberValidation(
        model.taxPayerNumber,
        model.region || authContext.state.session?.selectedAffiliation?.entity.region
      );
      taxPayerNumberValid = taxPayerValidation.valid;
      if (taxPayerValidation.error) {
        setTaxPayerNumberError(
          t([taxPayerValidation.error], {
            digits: taxPayerValidation.digits,
            region: taxPayerValidation.region
              ? t(translations.regions[taxPayerValidation.region])
              : '',
          })
        );
      } else {
        setTaxPayerNumberError(undefined);
      }

      // National Health Service Number validation
      const patientNumberValidation = nationalHealthServiceNumberValidation(
        model.nationalHealthServiceNumber,
        model.region || authContext.state.session?.selectedAffiliation?.entity.region
      );
      nationalHealthServiceNumberValid = patientNumberValidation.valid;
      if (patientNumberValidation.error) {
        setNationalHealthServiceNumberError(
          t([patientNumberValidation.error], {
            digits: patientNumberValidation.digits,
            region: patientNumberValidation.region
              ? t(translations.regions[patientNumberValidation.region])
              : '',
          })
        );
      } else {
        setNationalHealthServiceNumberError(undefined);
      }

      // Social Security Number validation
      const socialNumberValidation = socialSecurityNumberValidation(
        model.socialSecurityNumber,
        model.region || authContext.state.session?.selectedAffiliation?.entity.region
      );
      socialSecurityNumberValid = socialNumberValidation.valid;
      if (socialNumberValidation.error) {
        setSocialSecurityNumberError(
          t([socialNumberValidation.error], {
            digits: socialNumberValidation.digits,
            region: socialNumberValidation.region
              ? t(translations.regions[socialNumberValidation.region])
              : '',
          })
        );
      } else {
        setSocialSecurityNumberError(undefined);
      }

      // National ID Number validation
      const nationalNumberValidation = nationalIdNumberValidation(
        model.nationalIdNumber,
        model.region || authContext.state.session?.selectedAffiliation?.entity.region
      );
      nationalIdNumberValid = nationalNumberValidation.valid;
      if (nationalNumberValidation.error) {
        setNationalIdNumberError(
          t(nationalNumberValidation.error, {
            digits: nationalNumberValidation.digits,
            letters: nationalNumberValidation.letters,
            region: nationalNumberValidation.region
              ? t(translations.regions[nationalNumberValidation.region])
              : '',
          })
        );
      } else {
        setNationalIdNumberError(undefined);
        setShowNationalIdNumberError(false);
      }

      // Entity validation
      if (
        props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator &&
        !model.id &&
        !entity
      ) {
        // Errors
        if (showEntityError) {
          setEntityError(t(translations.user_form.errors.edit_form_validation_invalid_field));
        }

        // Invalid
        entityValid = false;
      } else {
        // No errors
        if (entityError !== undefined) {
          setEntityError(undefined);
        }

        // Valid
        entityValid = true;
      }

      // Room validation
      const validationRoom = roomValidation(modelAffiliationData?.room);
      roomValid = validationRoom.valid;
      if (validationRoom.error) {
        setRoomError(t(validationRoom.error, {max: validationRoom.max}));
      } else {
        setRoomError(undefined);
      }

      // Process number validation
      if (
        modelAffiliationData?.processNumber &&
        modelAffiliationData?.processNumber?.length > PROCESS_NUMBER_MAX_CHARACTERS
      ) {
        setProcessNumberError(
          t(translations.user_form.errors.form_validation_process_number_max_chars, {
            max: PROCESS_NUMBER_MAX_CHARACTERS,
          })
        );
        processNumberValid = false;
      } else {
        setProcessNumberError(undefined);
        processNumberValid = true;
      }

      // Recurring fee validation
      if (
        modelAffiliationData?.recurringFee &&
        String(modelAffiliationData.recurringFee).length > RECURRING_FEE_MAX_CHARACTERS
      ) {
        setRecurringFeeError(
          t(translations.user_form.errors.form_validation_recurring_fee_max_chars, {
            max: RECURRING_FEE_MAX_CHARACTERS,
          })
        );
        recurringFeeValid = false;
      } else {
        setRecurringFeeError(undefined);
        recurringFeeValid = true;
      }

      // Additional info validation
      if (
        modelAffiliationData?.additionalInfo &&
        (modelAffiliationData.additionalInfo.length > ADITIONAL_INFO_MAX_CHARACTERS ||
          modelAffiliationData.additionalInfo.length === 0)
      ) {
        setAdditionalInfoError(
          t(translations.user_form.errors.form_validation_aditional_info_max_chars, {
            max: ADITIONAL_INFO_MAX_CHARACTERS,
          })
        );
        aditionalInfoValid = false;
      } else {
        setAdditionalInfoError(undefined);
        aditionalInfoValid = true;
      }
      // #endregion

      // #region ----- Data changed -----
      // Validate phoneNumber
      let phoneNumberChanged = false;
      if (
        (!originalModel.phoneNumber && model.phoneNumber) ||
        (originalModel.phoneNumber && !model.phoneNumber) ||
        (model.phoneNumber &&
          originalModel.phoneNumber &&
          `${countryCode.phone}${model.phoneNumber}` !== originalModel.phoneNumber)
      ) {
        phoneNumberChanged = true;
      }

      // Validate birthday
      let birthdayChanged = false;
      if (
        (model.birthday && !originalModel.birthday) ||
        (!model.birthday && originalModel.birthday) ||
        (model.birthday &&
          originalModel.birthday &&
          !moment(model.birthday).isSame(originalModel.birthday, 'day'))
      ) {
        birthdayChanged = true;
      }

      // Entity
      let entityIdChanged = false;
      if (
        props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator &&
        !model.id &&
        entity
      ) {
        entityIdChanged = true;
      }

      // Room
      let roomChanged = false;
      if (model.id && modelAffiliationData?.room !== originalModelAffiliationData?.room) {
        roomChanged = true;
      }

      // Process number
      let processNumberChanged = false;
      if (
        modelAffiliationData?.processNumber !== originalModelAffiliationData?.processNumber &&
        modelAffiliationData?.processNumber
      ) {
        processNumberChanged = true;
      }

      // Recurring fee
      let recurringFeeChanged = false;
      if (
        modelAffiliationData?.recurringFee !== originalModelAffiliationData?.recurringFee &&
        modelAffiliationData?.recurringFee
      ) {
        recurringFeeChanged = true;
      }

      // Social Responses
      let socialResponsesChanges = false;
      if (props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver) {
        if (
          modelAffiliationData?.socialResponses?.length !==
            originalModelAffiliationData?.socialResponses?.length ||
          modelAffiliationData?.socialResponses?.some(
            (r) => !originalModelAffiliationData?.socialResponses?.includes(r)
          )
        ) {
          socialResponsesChanges = true;
        }
      }

      if (
        (model.name || '') !== (originalModel.name || '') ||
        (model.displayName || '') !== (originalModel.displayName || '') ||
        (model.email || '') !== (originalModel.email || '') ||
        (model.taxPayerNumber || '') !== (originalModel.taxPayerNumber || '') ||
        (model.nationalHealthServiceNumber || '') !==
          (originalModel.nationalHealthServiceNumber || '') ||
        (model.socialSecurityNumber || '') !== (originalModel.socialSecurityNumber || '') ||
        (model.nationalIdNumber || '') !== (originalModel.nationalIdNumber || '') ||
        (model.address || '') !== (originalModel.address || '') ||
        model.isActive !== originalModel.isActive ||
        model.picture !== originalModel.picture ||
        model.gender !== originalModel.gender ||
        model.languageCode !== originalModel.languageCode ||
        model.religion !== originalModel.religion ||
        model.region !== originalModel.region ||
        model.maritalStatus !== originalModel.maritalStatus ||
        model.educationLevel !== originalModel.educationLevel ||
        socialResponsesChanges ||
        model.occupationId !== originalModel.occupationId ||
        (model.occupationDescription || '') !== (originalModel.occupationDescription || '') ||
        model.socialNetworkCityId !== originalModel.socialNetworkCityId ||
        birthdayChanged ||
        phoneNumberChanged ||
        entityIdChanged ||
        roomChanged ||
        processNumberChanged ||
        recurringFeeChanged ||
        modelAffiliationData?.additionalInfo !== originalModelAffiliationData?.additionalInfo ||
        modelAffiliationData?.admissionDate !== originalModelAffiliationData?.admissionDate ||
        modelAffiliationData?.departureDate !== originalModelAffiliationData?.departureDate
      ) {
        // Set it
        dataWasUpdated = true;

        // Set it
        setDataChanged(true);
      } else {
        // Set it
        dataWasUpdated = false;

        // Set it
        setDataChanged(false);
      }
      // #endregion

      // #region ----- Submit button validation -----
      if (
        props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
        props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator
      ) {
        // professional caregiver or admin is editing senior
        if (model.id) {
          // Edit
          if (
            fullNameValid &&
            displayNameValid &&
            phoneNumberValid &&
            emailValid &&
            taxPayerNumberValid &&
            dataWasUpdated &&
            roomValid &&
            nationalHealthServiceNumberValid &&
            socialSecurityNumberValid &&
            processNumberValid &&
            recurringFeeValid &&
            aditionalInfoValid &&
            nationalIdNumberValid &&
            modelAffiliationData?.admissionDate
          ) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        } else {
          // Create
          if (
            fullNameValid &&
            displayNameValid &&
            phoneNumberValid &&
            emailValid &&
            taxPayerNumberValid &&
            entityValid &&
            roomValid &&
            nationalHealthServiceNumberValid &&
            socialSecurityNumberValid &&
            processNumberValid &&
            recurringFeeValid &&
            aditionalInfoValid &&
            nationalIdNumberValid &&
            modelAffiliationData?.admissionDate
          ) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        }
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }

      // Check if assigned roles changed
      if (props.canEditRole && props.entityId) {
        if (
          originalRolesIds?.length === rolesIds?.length &&
          originalRolesIds?.every((roleId) => rolesIds?.includes(roleId))
        ) {
          // No role changes

          // data above wasn't updated also
          if (!dataWasUpdated) {
            setDataChanged(false);
            setSubmitButtonDisabled(true);
          }

          // Skip
          return;
        }

        setDataChanged(true);
        setSubmitButtonDisabled(false);
      }
      // #endregion
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    originalModel,
    modelAffiliationData,
    originalModelAffiliationData,
    countryCode,
    entity,
    fullNameError,
    displayNameError,
    phoneNumberError,
    emailError,
    entityError,
    taxPayerNumberError,
    nationalHealthServiceNumberError,
    socialSecurityNumberError,
    showFullNameError,
    showDisplayNameError,
    showPhoneNumberError,
    showEmailError,
    showTaxPayerNumberError,
    showEntityError,
    showAdditionalInfoError,
    showProcessNumberError,
    showRecurringFeeError,
    showNationalIdNumberError,
    props.userFormType,
    originalRolesIds,
    rolesIds,
    roomError,
    showRoomError,
    showNationalHealthServiceNumberError,
    showSocialSecurityNumberError,
  ]);

  // Handle submit
  const handleSubmit = async () => {
    // Handle Data
    const [newUserData, ,] = await Promise.all([
      // user data
      handleSaveUser(),

      // user roles data
      props.canEditRole && props.entityId ? handleUpdateRolesIds() : undefined,
    ]);

    // Reset Context
    if (originalModel) {
      officeContext.setUserForm(
        newUserData ?? originalModel,
        officeContext.state.userForm?.affiliation
          ? {
              ...officeContext.state.userForm.affiliation,
              rolesIds,
            }
          : undefined
      );
    }
  };

  // Handle submit
  const handleSaveUser = async (): Promise<User | undefined> => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Loading);

      // Format phone number
      let phoneNumber: string | undefined;

      if (model.phoneNumber) {
        try {
          // Parse phone number
          const parsedPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
            `${countryCode.phone}${model.phoneNumber}`
          );

          // Check if the phone number is valid
          if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
            // Set it
            phoneNumber = parsedPhoneNumber.number.toString();
          } else {
            // Error
            throw new Error('Phone number parse failed.');
          }
        } catch (error) {
          // Error
          console.debug('Could not process phone number.', error);
        }
      }

      if (model.id) {
        // -----> Edit senior or caregiver <-----

        // Profile picture to upload
        let processedProfilePicture: string | undefined;
        if (profilePictureFile) {
          processedProfilePicture = await processProfilePicture(model.id, profilePictureFile);
        } else {
          processedProfilePicture = model.picture;
        }

        // Request body
        // Note: sending null clears(empty) the data on db. undefined are discarded.
        const requestBody: PutUser = {
          type: model.type,
          isActive: model.isActive,
          product: model.product,
          picture: processedProfilePicture,
          name: model.name,
          displayName: model.displayName,
          gender: model.gender,
          languageCode: model.languageCode,
          email: model.email,
          birthday: model.birthday, // UTC
          phoneNumber: phoneNumber,
          address: model.address,
          religion: model.religion,
          occupationId: model.occupationId,
          occupationDescription: model.occupationDescription,
          socialNetworkCityId: model.socialNetworkCityId,
          region: model.region,
          taxPayerNumber: model.taxPayerNumber,
          nationalHealthServiceNumber: model.nationalHealthServiceNumber,
          socialSecurityNumber: model.socialSecurityNumber,
          educationLevel: model.educationLevel,
          maritalStatus: model.maritalStatus,
          nationalIdNumber: model.nationalIdNumber,
        };

        // Edit user
        const updatedUser = await UsersRepository.instance
          .updateById(model.id, requestBody)
          .then((data) => {
            // Dispatch updated senior data to context - as to update the senior sidepanel
            if (props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver) {
              const socialResponsesData = entityState.userEntitySocialResponses.filter((r) =>
                modelAffiliationData?.socialResponses?.includes(r.id)
              );
              if (
                props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver &&
                modelAffiliationData &&
                socialResponsesData
              ) {
                seniorSidebarContext.updateSeniorData(data, socialResponsesData);
              } else {
                seniorSidebarContext.updateSeniorData(data);
              }
            }
            return data;
          });

        // Update the senior affiliation - `socialResponses`, `room`...
        if (
          (props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
            props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator) &&
          modelAffiliationData
        ) {
          await EntitiesRepository.instance.updateAffiliation(props.entityId!, model.id, {
            socialResponses: modelAffiliationData.socialResponses,
            room: modelAffiliationData.room || null,
            admissionDate: modelAffiliationData.admissionDate,
            departureDate: modelAffiliationData.departureDate || null,
            additionalInfo: modelAffiliationData.additionalInfo || null,
            processNumber: modelAffiliationData.processNumber || null,
            recurringFee: modelAffiliationData.recurringFee || null,
          });
        }

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_form.success.edit_senior_success_message),
        });

        // Update submit button state
        officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);

        return updatedUser;
      } else if (
        (props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
          props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator) &&
        !model.id
      ) {
        // -----> Create <-----

        // Check entity id
        if (!entity?.id) {
          // Error
          throw new Error('Empty entity id.');
        }

        // Request body
        const requestBody: PostSenior = {
          type: model.type,
          isActive: model.isActive,
          product: model.product,
          name: model.name,
          displayName: model.displayName,
          gender: model.gender,
          languageCode: model.languageCode,
          email: model.email,
          birthday: model.birthday, // UTC
          phoneNumber: phoneNumber,
          address: model.address,
          religion: model.religion,
          occupationId: model.occupationId,
          occupationDescription: model.occupationDescription,
          socialNetworkCityId: model.socialNetworkCityId,
          region: model.region,
          taxPayerNumber: model.taxPayerNumber,
          nationalHealthServiceNumber: model.nationalHealthServiceNumber,
          socialSecurityNumber: model.socialSecurityNumber,
          educationLevel: model.educationLevel,
          maritalStatus: model.maritalStatus,
          nationalIdNumber: model.nationalIdNumber,
        };

        // Create senior
        const newSenior = await UsersRepository.instance.createSenior(entity.id, requestBody);

        // Update the senior affiliation - `socialResponses`, `room`...
        if (
          (props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
            props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator) &&
          modelAffiliationData &&
          newSenior.id
        ) {
          await EntitiesRepository.instance.updateAffiliation(entity.id, newSenior.id, {
            socialResponses: modelAffiliationData.socialResponses,
            room: modelAffiliationData.room || null,
            admissionDate: modelAffiliationData.admissionDate,
            departureDate: modelAffiliationData.departureDate || null,
            additionalInfo: modelAffiliationData.additionalInfo || null,
            processNumber: modelAffiliationData.processNumber || null,
            recurringFee: modelAffiliationData.recurringFee || null,
          });
        }

        // Senior profile
        let seniorProfile: User = newSenior;

        // Update the senior with the profile picture if it's necessary
        if (profilePictureFile) {
          // We will not make the creation of the senior fail if the picture
          // is not created because the senior was actually created previously.
          try {
            const processedProfilePicture = await processProfilePicture(
              seniorProfile.id || '',
              profilePictureFile
            );

            // Request body
            const updatedSeniorRequestBody: PutUser = {
              product: seniorProfile.product,
              isActive: seniorProfile.isActive,
              name: seniorProfile.name,
              type: seniorProfile.type,
              picture: processedProfilePicture,
            };

            // Update the senior
            const updatedSenior: User = await UsersRepository.instance.updateById(
              seniorProfile.id || '',
              updatedSeniorRequestBody
            );

            // Update it
            seniorProfile = {...updatedSenior};
          } catch (error) {
            // Log
            console.debug('🔴 Could not update the senior profile picture.', error);
          }
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}${seniorProfile.id}`
              : `${props.redirectUrl}/${seniorProfile.id}`
          );

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.user_form.success.create_senior_success_message, {
              name: seniorProfile.name,
            }),
          });

          // Update submit button state
          officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);
        }, 500);

        return newSenior;
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not edit/create the user.', error);

      // Check if it's a "licenses-number-exceeded" error
      if (
        error &&
        error.response &&
        error.response.data &&
        Array.isArray(error.response.data.errors) &&
        error.response.data.errors[0] &&
        error.response.data.errors[0].code === 'licenses-number-exceeded'
      ) {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: model.id
            ? t(translations.user_form.errors.activate_senior_licenses_number_exceeded_error)
            : t(translations.user_form.errors.create_active_senior_licenses_number_exceeded_error),
        });
      }

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  const handleUpdateRolesIds = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !props.canEditRole) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Loading);

      // Check ids and affiliation type
      if (!entity?.id || !originalModel.id) {
        // Error
        throw new Error('Empty ids.');
      }

      // Update the affiliation
      await EntitiesRepository.instance.updateAffiliation(entity.id, originalModel.id, {
        rolesIds,
      });

      // If the user updated their own affiliation, we need to update
      // the session because some data may have changed.
      // If an error occurs, it's not a problem.
      try {
        await authContext.refreshSessionData(
          authContext.state.session?.selectedRelationship?.id,
          authContext.state.session?.selectedAffiliation?.id
        );
      } catch (error) {
        // Error
        console.debug('🔴 Could not refresh session.', error);
      }

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.user_form.success.edit_caregiver_roles_ids_success_message),
      });

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Success);
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit the affiliation type.', error);

      // Update submit button state
      officeContext.setUserFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  // ----- Handle change ------
  // Handle text fields
  const handleChangeTextField = (field: keyof User, value: boolean | string | undefined) => {
    // Process value
    let finalValue: string | boolean | undefined | null = value;

    if (value === undefined || value === '') {
      // Note: To actually empty data in DB we need to send null
      finalValue = null;
    }

    // Set it
    setModel(
      (u) =>
        ({
          ...u,
          [field]: finalValue,
        }) as User
    );
  };

  // Handle profile picture change
  const handleChangeProfilePicture = (image?: {base64: string; file: File}) => {
    if (image) {
      // Update profile picture and model
      setProfilePictureFile(image.file);
      handleChangeTextField('picture', image.base64);
    } else {
      // Clear
      setProfilePictureFile(undefined);
      handleChangeTextField('picture', undefined);
    }
  };

  // ----- Handle change ------

  // Fetch entities
  const handleFetchEntities = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<SearchSelectInputItem>>; total: number}> => {
    // Get entities
    const response = await EntitiesRepository.instance.get(skip, limit, search);

    // Return
    return {
      data: response.data.map((obj) => {
        return {
          id: obj.id || '',
          name: obj.name || '',
          item: {id: obj.id || '', name: obj.name || '', picture: obj.picture},
          avatar: obj.picture,
        };
      }),
      total: response.totalCount,
    };
  };

  // Strings to use in the form
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';
  switch (props.userFormType) {
    case UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver:
    case UserFormType.CreateEditRemoveSenior_SystemAdministrator:
      routerDialogTitle = model?.id
        ? t(translations.user_form.senior_router_prompt_edit_title, {
            name: originalModel?.name || '',
          })
        : t(translations.user_form.senior_router_prompt_create_title, {
            name: model?.name || '',
          });
      routerDialogContent = model?.id
        ? t(translations.user_form.senior_router_prompt_edit_message)
        : t(translations.user_form.senior_router_prompt_create_message);
      routerCancelToast = model?.id
        ? t(translations.user_form.senior_edit_cancel_toast, {
            name: originalModel?.name || '',
          })
        : t(translations.user_form.senior_create_cancel_toast);
      break;
    default:
      break;
  }

  // Loading
  if (!model || officeContext.state.userFormLoadingState) {
    return (
      <ContentPage>
        <div className="user-senior-form">
          <LoadingContent className="loading" />
        </div>
      </ContentPage>
    );
  }

  // Form
  return (
    <ContentPage>
      <form className="user-senior-form">
        {/* ----- PROFILE PICTURE, ADMISSION DATE, PROCESS NUMBER INPUTS ----- */}
        <div className="row">
          {/* --- PROFILE PICTURE --- */}
          <>
            {props.userFormType ===
            UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ? (
              <div className="profile-picture" data-edit="false">
                <Avatar
                  alt={model.name}
                  src={model.picture}
                  sx={{
                    width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                    height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                  }}
                >
                  <Person
                    sx={{
                      fontSize:
                        avatarSizesAndCoefficients.avatarSizes.extraGiant *
                        avatarSizesAndCoefficients.iconSizeCoefficient,
                    }}
                  />
                </Avatar>
              </div>
            ) : (
              <EditPicture
                type="user"
                alt={t(translations.user_form.profile_picture_input)}
                src={model.picture}
                handleChange={handleChangeProfilePicture}
                disabled={
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
                size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
                alertMessage={t(translations.user_form.profile_picture_info_message)}
                useFileCropper
                clearable
              />
            )}
            {props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator &&
              !model.id && (
                <FlatSearchSelectInput<SearchSelectInputItem>
                  label={t(translations.user_form.entity_input)}
                  value={
                    entity
                      ? {
                          id: entity.id,
                          name: entity.name,
                          item: entity,
                          avatar: entity.picture,
                        }
                      : undefined
                  }
                  handleChange={(value?: SearchSelectOption<SearchSelectInputItem>) =>
                    setEntity(value?.item || undefined)
                  }
                  handleFetchData={handleFetchEntities}
                  handleBlur={() => setShowEntityError(true)}
                  fetchWhenEmpty={true}
                  disabled={
                    officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                    officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  error={entityError}
                  required={true}
                  disableClear={true}
                  avatar={{isIcon: false, type: 'entity'}}
                />
              )}
          </>

          {/* ----- ADMISSION DATE, PROCESS NUMBER INPUTS ----- */}
          <div className="row short-gap">
            {/* --- EMPTY SPACER --- */}
            <div />

            {/* --- ADMISSION DATE, PROCESS NUMBER --- */}
            <div className="column">
              {/* --- ADMISSION DATE --- */}
              <FlatSelectDate
                label={t(translations.user_form.admission_date_input)}
                placeholder={t(translations.user_form.date_placeholder_input)}
                value={
                  modelAffiliationData?.admissionDate
                    ? new Date(modelAffiliationData?.admissionDate)
                    : undefined
                }
                required
                onChange={(date) => {
                  // @ts-ignore
                  setModelAffiliationData({
                    ...modelAffiliationData,
                    admissionDate: date
                      ? moment(date)
                          .utc()
                          .set({
                            hours: 0,
                            minutes: 0,
                            seconds: 0,
                            milliseconds: 0,
                          })
                          .toISOString()
                      : undefined,
                  });
                }}
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disableFuture
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />

              {/* --- PROCESS NUMBER--- */}
              <FlatInput
                type="text"
                label={t(translations.user_form.process_number_input)}
                placeholder={t(translations.user_form.process_number_input_placeholder)}
                value={modelAffiliationData?.processNumber || ''}
                onChange={(e) => {
                  // @ts-ignore
                  setModelAffiliationData({
                    ...modelAffiliationData,
                    processNumber: e.currentTarget.value ? e.currentTarget.value : undefined,
                  });
                }}
                onBlur={() => setShowProcessNumberError(true)}
                errors={processNumberError}
                counter={
                  modelAffiliationData?.processNumber && processNumberError
                    ? {
                        current: modelAffiliationData?.processNumber.length,
                        max: PROCESS_NUMBER_MAX_CHARACTERS,
                      }
                    : undefined
                }
                disabled={
                  props.userFormType ===
                    UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                  officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
                }
                disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
              />
            </div>
          </div>
        </div>

        <div className="divider" />

        {/* ----- FULL NAME, STATUS AND ROOM INPUTS ----- */}
        <div className="row">
          {/* --- FULL NAME --- */}
          <FlatInput
            label={t(translations.user_form.full_name_input)}
            placeholder={t(translations.user_form.input_placeholder, {
              type: String(t(translations.user_form.full_name_input)).toLowerCase(),
            })}
            value={model.name}
            onChange={(e) => handleChangeTextField('name', e.currentTarget.value)}
            onBlur={() => setShowFullNameError(true)}
            multiline
            rows={2}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            errors={fullNameError}
            counter={
              fullNameError
                ? {
                    current: model.name?.length ?? 0,
                    max: FULL_NAME_MAX_CHARACTERS,
                  }
                : undefined
            }
            required
            type="text"
          />

          <div className="row short-gap">
            {/* --- STATUS --- */}
            <FlatSelectDropdown
              label={t(translations.user_form.state_input, {
                role:
                  props.userFormType ===
                    UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
                  props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator
                    ? t(translations.user_types.senior)
                    : t(translations.user_types.caregiver),
              })}
              value={
                model.isActive !== null
                  ? {
                      key: String(model.isActive),
                      label: model.isActive
                        ? t(translations.user_form.state_value_true)
                        : t(translations.user_form.state_value_false),
                    }
                  : undefined
              }
              options={[
                {key: 'true', label: t(translations.user_form.state_value_true)},
                {key: 'false', label: t(translations.user_form.state_value_false)},
              ]}
              onChange={(option) => handleChangeTextField('isActive', JSON.parse(option.key))}
              required
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            />

            {/* --- ROOM --- */}
            <FlatInput
              type="text"
              label={t(translations.user_form.room_input)}
              placeholder={t(translations.user_form.room_placeholder_input)}
              value={modelAffiliationData?.room || ''}
              onChange={(e) => {
                // @ts-ignore
                setModelAffiliationData({
                  ...modelAffiliationData,
                  room: e.currentTarget.value ? e.currentTarget.value : undefined,
                });
              }}
              onBlur={() => setShowRoomError(true)}
              errors={roomError}
              counter={
                modelAffiliationData?.room && roomError
                  ? {
                      current: modelAffiliationData?.room.length,
                      max: ROOM_NAME_MAX_CHARACTERS,
                    }
                  : undefined
              }
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            />
          </div>
        </div>

        {/* ----- SHORT NAME, SOCIAL RESPONSES ----- */}
        <div className="row">
          {/* --- SHORT NAME --- */}
          <FlatInput
            type="text"
            label={t(translations.user_form.display_name_input)}
            placeholder={t(translations.user_form.input_placeholder, {
              type: String(t(translations.user_form.display_name_input)).toLowerCase(),
            })}
            value={model.displayName || ''}
            onChange={(e) => handleChangeTextField('displayName', e.currentTarget.value)}
            onBlur={() => setShowDisplayNameError(true)}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            counter={
              displayNameError
                ? {
                    current: model.displayName?.length ?? 0,
                    max: DISPLAY_NAME_MAX_CHARACTERS,
                  }
                : undefined
            }
            required
            errors={displayNameError}
          />

          {/* --- SOCIAL RESPONSES --- */}
          <FlatMultiTextSelect
            label={t(translations.entity_form.social_responses_input)}
            placeholder={t(translations.entity_form.social_responses_placeholder_input)}
            value={
              modelAffiliationData?.socialResponses
                ? modelAffiliationData?.socialResponses.map((s) => {
                    const options = convertArrayToIndex<Option>(
                      entityState.availableSocialResponses.map((r) => {
                        return {key: r.id, label: getLocalizedFieldValue(r, 'abbreviation')};
                      }) || [],
                      'key'
                    );

                    return {
                      key: s,
                      label: options[s]?.label ?? s,
                    };
                  })
                : []
            }
            options={
              props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver
                ? entityState.userEntitySocialResponses.map((r) => {
                    return {key: r.id, label: getLocalizedFieldValue(r, 'abbreviation')};
                  })
                : []
            }
            onChange={(selected) => {
              // @ts-ignore
              setModelAffiliationData({
                ...modelAffiliationData,
                socialResponses: Array.isArray(selected) ? selected.map((s) => s.key) : undefined,
              });
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- BIRTHDAY, RECURRING FEE INPUTS ----- */}
        <div className="row">
          {/* --- BIRTHDAY --- */}
          <FlatSelectDate
            label={t(translations.user_form.birthday_input)}
            value={model.birthday ? new Date(model.birthday) : undefined}
            placeholder={t(translations.user_form.date_placeholder_input)}
            allowClear
            disableFuture
            timezone="UTC"
            onChange={(date) => {
              const dateISO = date
                ? moment(date)
                    .utc()
                    .set({hours: 0, minutes: 0, seconds: 0, milliseconds: 0})
                    .toISOString()
                : undefined;

              handleChangeTextField('birthday', dateISO);
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />

          {/* --- RECURRING FEE --- */}
          <FlatInput
            type="number"
            autoComplete="off"
            label={t(translations.user_form.recurring_fee_input)}
            placeholder={t(translations.user_form.recurring_fee_input_placeholder)}
            value={modelAffiliationData?.recurringFee}
            onChange={(e) => {
              // @ts-ignore
              setModelAffiliationData({
                ...modelAffiliationData,
                recurringFee: Number(e.currentTarget.value) || undefined,
              });
            }}
            onBlur={() => setShowRecurringFeeError(true)}
            errors={recurringFeeError}
            counter={
              modelAffiliationData?.recurringFee && recurringFeeError
                ? {
                    current: String(modelAffiliationData?.recurringFee).length,
                    max: RECURRING_FEE_MAX_CHARACTERS,
                  }
                : undefined
            }
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- GENDER, MARITAL STATUS, ADITIONAL INFO INPUTS -----*/}
        <div className="row">
          <div className="column">
            {/* -- GENDER --- */}
            <FlatSelectDropdown
              label={t(translations.user_form.gender_input)}
              placeholder={t(translations.user_form.gender_placeholder_input)}
              value={
                model.gender
                  ? {
                      key: model.gender,
                      label: t(translations.genders[model.gender as UserGender]),
                    }
                  : undefined
              }
              options={genders.map((gender) => {
                return {key: gender.type, label: t(gender.translation)};
              })}
              onChange={(e) => {
                handleChangeTextField('gender', e.key);
              }}
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            />

            {/* --- MARITAL STATUS --- */}
            <FlatSelectDropdown
              label={t(translations.user_form.marital_status_input)}
              placeholder={t(translations.user_form.marital_status_input_placeholder)}
              clearable
              value={
                model.maritalStatus
                  ? {
                      key: model.maritalStatus,
                      label: t(translations.marital_statuses[model.maritalStatus as MaritalStatus]),
                    }
                  : undefined
              }
              options={maritalStatuses.map((ms) => {
                return {key: ms.type, label: t(ms.translation)};
              })}
              onChange={(e) => {
                handleChangeTextField('maritalStatus', e.key);
              }}
              disabled={
                props.userFormType ===
                  UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
                officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
              }
              disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            />
          </div>

          {/* --- ADITIONAL INFO --- */}
          <FlatInput
            type="text"
            label={t(translations.user_form.aditional_info_input)}
            placeholder={t(translations.user_form.additional_info_input_placeholder)}
            value={modelAffiliationData?.additionalInfo || ''}
            onChange={(e) => {
              // @ts-ignore
              setModelAffiliationData({
                ...modelAffiliationData,
                additionalInfo: e.currentTarget.value,
              });
            }}
            onBlur={() => setShowAdditionalInfoError(true)}
            multiline
            sx={{
              '& .MuiInputBase-inputMultiline': {
                height: '122px !important',
              },
            }}
            errors={additionalInfoError}
            counter={
              additionalInfoError
                ? {
                    current: modelAffiliationData?.additionalInfo?.length ?? 0,
                    max: ADITIONAL_INFO_MAX_CHARACTERS,
                  }
                : undefined
            }
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        <div className="divider" />

        {/* ----- ADDRESS, EDUCATION LEVEL, PHONE NUMBER, LANGUAGE INPUTS ----- */}
        <div className="row">
          {/* --- ADDRESS --- */}
          <FlatInput
            type="text"
            label={t(translations.user_form.address_input)}
            placeholder={t(translations.user_form.address_placeholder_input)}
            value={model.address || ''}
            onChange={(e) => handleChangeTextField('address', e.currentTarget.value)}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            multiline
            rows={2}
          />

          {/* --- EDUCATION LEVEL --- */}
          <FlatSelectDropdown
            label={t(translations.user_form.education_level_input)}
            placeholder={t(translations.user_form.education_level_input_placeholder)}
            clearable
            value={
              model.educationLevel
                ? {
                    key: model.educationLevel,
                    label: t(translations.education_levels[model.educationLevel as EducationLevel]),
                  }
                : undefined
            }
            options={educationLevels.map((el) => {
              return {key: el.type, label: t(el.translation)};
            })}
            onChange={(e) => {
              handleChangeTextField('educationLevel', e.key);
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- PHONE NUMBER, LANGUAGE INPUTS ----- */}
        <div className="row">
          {/* --- PHONE NUMBER --- */}
          <PhoneNumberInput
            label={
              props.userFormType === UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver ||
              props.userFormType === UserFormType.CreateEditRemoveSenior_SystemAdministrator
                ? t(translations.user_form.mobile_and_landline_phone_number_input)
                : t(translations.user_form.mobile_phone_number_input)
            }
            countryCode={countryCode}
            phoneNumber={model.phoneNumber || ''}
            handleChangeCountryCode={(value) => setCountryCode(value)}
            handleChangePhoneNumber={(value) => handleChangeTextField('phoneNumber', value)}
            placeholder={t(translations.user_form.mobile_phone_number_placeholder_input)}
            handleBlur={() => setShowPhoneNumberError(true)}
            error={phoneNumberError}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />

          {/* --- LANGUAGE --- */}
          <FlatSelectDropdown
            label={t(translations.user_form.language_input)}
            placeholder={t(translations.user_form.language_placeholder_input)}
            clearable
            value={
              model.languageCode
                ? {
                    key: model.languageCode,
                    label: t(translations.languages[model.languageCode as BackendLanguageTag]),
                  }
                : undefined
            }
            options={languages.map((language) => {
              return {key: language.type, label: t(language.translation)};
            })}
            onChange={(e) => {
              handleChangeTextField('languageCode', e.key);
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- EMAIL, REGION INPUTS ----- */}
        <div className="row">
          {/* --- EMAIL --- */}
          <FlatInput
            type="email"
            label={t(translations.user_form.email_input)}
            placeholder={t(translations.user_form.email_placeholder_input)}
            value={model.email || ''}
            onChange={(e) => handleChangeTextField('email', e.currentTarget.value)}
            onBlur={() => setShowEmailError(true)}
            errors={emailError}
            disabled={
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading ||
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator
            }
            disabledTooltip={
              props.userFormType ===
              UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator
                ? t(translations.user_form.input_read_only_tooltip)
                : undefined
            }
          />

          {/* --- REGION --- */}
          <FlatSelectDropdown
            label={t(translations.user_form.region_input)}
            placeholder={t(translations.user_form.region_placeholder_input)}
            clearable
            value={
              model.region
                ? {
                    key: model.region,
                    label: t(translations.regions[model.region as RegionCode]),
                  }
                : undefined
            }
            options={regions.map((region) => {
              return {key: region.type, label: t(region.translation)};
            })}
            onChange={(e) => {
              handleChangeTextField('region', e.key);
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- OCCUPATION, RELIGION INPUTS ----- */}
        <div className="row">
          {/* --- OCCUPATION INPUT --- */}
          <OccupationInput
            label={t(translations.user_form.profession_input)}
            value={occupation}
            handleChange={(value?: OccupationOption) => {
              setOccupation(value);
              handleChangeTextField('occupationId', value?.occupationId);
            }}
            required={false}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
            userType={UserType.Senior}
          />

          {/* --- RELIGION --- */}
          <FlatSelectDropdown
            label={t(translations.user_form.religion_input)}
            placeholder={t(translations.user_form.religion_placeholder_input)}
            clearable
            value={
              model.religion
                ? {
                    key: model.religion,
                    label: t(translations.religions[model.religion as Religion]),
                  }
                : undefined
            }
            options={religions.map((religion) => {
              return {key: religion.type, label: t(religion.translation)};
            })}
            onChange={(e) => {
              handleChangeTextField('religion', e.key);
            }}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        {/* ----- OCCUPATION DESCRIPTION, CITY INPUTS ----- */}
        <div className="row">
          {/* --- OCCUPATION DESCRIPTION INPUT --- */}
          <FlatInput
            type="text"
            label={t(translations.user_form.profession_description_input)}
            placeholder={t(translations.user_form.input_placeholder, {
              type: String(t(translations.user_form.profession_description_input)).toLowerCase(),
            })}
            value={model?.occupationDescription || ''}
            onChange={(e) =>
              handleChangeTextField('occupationDescription', e.currentTarget.value || undefined)
            }
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />

          {/* --- CITY INPUT --- */}
          <SocialNetworkCityInput
            label={t(translations.user_form.social_network_city_input)}
            value={socialNetworkCity}
            handleChange={(value?: SocialNetworkCityOption) => {
              setSocialNetworkCity(value);
              handleChangeTextField('socialNetworkCityId', value?.cityId);
            }}
            required={false}
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />
        </div>

        <div className="divider" />

        {/* ----- CITIZEN CARD, NATIONAL HEALTH SERVICE NUMBER INPUTS ----- */}
        <div className="row">
          {/* --- CITIZEN CARD --- */}
          <FlatInput
            type="text"
            autoComplete="off"
            label={t(translations.user_form.citizen_card_input)}
            placeholder={t(translations.user_form.citizen_card_input_placeholder)}
            value={model.nationalIdNumber || ''}
            onChange={(e) => handleChangeTextField('nationalIdNumber', e.currentTarget.value)}
            onBlur={() => setShowNationalIdNumberError(true)}
            errors={nationalIdNumberError}
            disabledTooltip={
              !model.region
                ? t(translations.user_form.input_disabled_by_unknown_region)
                : t(translations.user_form.input_read_only_tooltip)
            }
            disabled={
              !model.region ||
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
          />

          {/* --- NATIONAL HEALTH SERVICE NUMBER --- */}
          <FlatInput
            type="text"
            autoComplete="off"
            label={t(translations.user_form.patient_number_input)}
            placeholder={t(translations.user_form.patient_number_input_placeholder)}
            value={model.nationalHealthServiceNumber || ''}
            onChange={(e) =>
              handleChangeTextField('nationalHealthServiceNumber', e.currentTarget.value)
            }
            onBlur={() => setShowNationalHealthServiceNumberError(true)}
            errors={nationalHealthServiceNumberError}
            disabledTooltip={
              !model.region
                ? t(translations.user_form.input_disabled_by_unknown_region)
                : t(translations.user_form.input_read_only_tooltip)
            }
            disabled={
              !model.region ||
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
          />
        </div>

        {/* ----- TAX PAYER NUMBER, SOCIAL SECURITY NUMBER INPUTS ----- */}
        <div className="row">
          {/* -- TAX PAYER NUMBER INPUT --- */}
          <FlatInput
            type="text"
            autoComplete="off"
            label={t(translations.user_form.tax_payer_number_input)}
            placeholder={t(translations.user_form.tax_payer_number_input_placeholder)}
            value={model.taxPayerNumber || ''}
            onChange={(e) => handleChangeTextField('taxPayerNumber', e.currentTarget.value)}
            onBlur={() => setShowTaxPayerNumberError(true)}
            errors={taxPayerNumberError}
            disabledTooltip={
              !model.region
                ? t(translations.user_form.input_disabled_by_unknown_region)
                : t(translations.user_form.input_read_only_tooltip)
            }
            disabled={
              !model.region ||
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
          />

          {/* -- SOCIAL SECURITY NUMBER INPUT --- */}
          <FlatInput
            type="text"
            autoComplete="off"
            label={t(translations.user_form.social_security_number_input)}
            placeholder={t(translations.user_form.social_security_number_input_placeholder)}
            value={model.socialSecurityNumber || ''}
            onChange={(e) => handleChangeTextField('socialSecurityNumber', e.currentTarget.value)}
            onBlur={() => setShowSocialSecurityNumberError(true)}
            errors={socialSecurityNumberError}
            disabledTooltip={
              !model.region
                ? t(translations.user_form.input_disabled_by_unknown_region)
                : t(translations.user_form.input_read_only_tooltip)
            }
            disabled={
              !model.region ||
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
          />
        </div>

        <div className="divider" />

        {/* ----- DEPARTURE DATE INPUT ----- */}
        <div className="row">
          {/* --- DEPARTURE DATE --- */}
          <FlatSelectDate
            label={t(translations.user_form.departure_date_input)}
            placeholder={t(translations.user_form.date_placeholder_input)}
            value={
              modelAffiliationData?.departureDate
                ? new Date(modelAffiliationData?.departureDate)
                : undefined
            }
            onChange={(date) => {
              // @ts-ignore
              setModelAffiliationData({
                ...modelAffiliationData,
                departureDate: date
                  ? moment(date)
                      .utc()
                      .set({
                        hours: 0,
                        minutes: 0,
                        seconds: 0,
                        milliseconds: 0,
                      })
                      .toISOString()
                  : undefined,
              });
            }}
            allowClear
            disabled={
              props.userFormType ===
                UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading
            }
            disabledTooltip={t(translations.user_form.input_read_only_tooltip)}
          />

          {/* --- EMPTY SPACER --- */}
          <div />
        </div>

        {/* ---- FORM CONFIRMATION BUTTONS ----- */}
        <div className="form-btns">
          <Button
            className="form-btn"
            variant="text"
            color="error"
            onClick={() => history.push(props.redirectUrl)}
          >
            {t(translations.user_form.cancel_button)}
          </Button>

          <SubmitButton
            className="form-btn"
            disabled={
              submitButtonDisabled ||
              officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading
            }
            state={officeContext.state.userFormSubmitButtonState || SubmitButtonState.Normal}
            onStateChange={(newState: SubmitButtonState) => {
              officeContext.setUserFormSubmitButtonState(newState);
            }}
            normalStateLabel={t(translations.user_form.save_button)}
            loadingStateLabel={t(translations.user_form.saving_button)}
            successStateLabel={t(translations.user_form.success.save_success_button)}
            errorStateLabel={t(translations.user_form.errors.error_on_save_button)}
            onClick={handleSubmit}
          />
        </div>
      </form>

      {/* Router */}
      <RouterPrompt
        // Show prompt if the data changed but make sure the path
        // is not changing because the user was deleted/created
        when={
          dataChanged &&
          officeContext.state.userFormDeleteButtonState !== SubmitButtonState.Loading &&
          officeContext.state.userFormSubmitButtonState !== SubmitButtonState.Loading
        }
        subtitle={routerDialogTitle}
        message={routerDialogContent}
        onOK={() => true}
        onCancel={() => false}
        toastNotification={{
          severity: 'info',
          message: routerCancelToast,
        }}
      />
    </ContentPage>
  );
};
